import { taxonExpandIri } from './_shared';
import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import { HydraCollectionResponse } from '@vs-shop/schemas/openapi/response';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import type { Schema as SchemaType } from 'effect';
import { Effect } from 'effect';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

import { HTTP_METHOD, PRODUCTS_ENDPOINT } from './_shared';

export type MakeGetProductsByTaxonDeps<ProductsByTaxon extends SchemaType.Top> = {
  readonly schemas: {
    readonly productsByTaxon: ProductsByTaxon;
  };
  readonly rawUrlEncode: (input: string) => string;
};

type GetProductsByTaxonProps = {
  taxonCode: string;
  page: number;
  itemsPerPage: number;
};

export const makeGetProductsByTaxon = <ProductsByTaxon extends SchemaType.Top>(
  deps: MakeGetProductsByTaxonDeps<ProductsByTaxon>
) => {
  const GetProductsByTaxonError = createBackendApiError({
    domain: 'Product',
    endpoint: 'GetProductsByTaxon',
    path: PRODUCTS_ENDPOINT,
    identifierField: 'taxonCode',
  });

  const getProductsByTaxonFetch = ({ taxonCode, page, itemsPerPage }: GetProductsByTaxonProps) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(PRODUCTS_ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/ld+json')
        .fetch({
          query: {
            page,
            itemsPerPage,
            inStock: true,
            taxon: taxonExpandIri(deps.rawUrlEncode, taxonCode),
          },
        });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${PRODUCTS_ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'product.taxonCode': taxonCode,
          'product.page': page,
          'product.itemsPerPage': itemsPerPage,
        },
      })
    );

  const getProductsByTaxon = (props: GetProductsByTaxonProps) =>
    getProductsByTaxonFetch(props).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.withSpan('ValidateResponse'),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) =>
            schemaBodyJsonClean(HydraCollectionResponse(deps.schemas.productsByTaxon))(response),
          404: (response) =>
            Effect.fail(
              new GetProductsByTaxonError.NotFoundError({ taxonCode: props.taxonCode, response })
            ),
          orElse: (response) =>
            Effect.fail(
              new GetProductsByTaxonError.ApiRequestError({
                taxonCode: props.taxonCode,
                response,
              })
            ),
        })
      ),
      Effect.tap((result) =>
        logApiOperation(
          ApiModule.ProductGet,
          'Debug',
          `Successfully resolved ${String(result['hydra:totalItems'])} products for taxon ${props.taxonCode}`
        )
      ),
      Effect.withSpan('api.products.getByTaxon', {
        kind: 'internal',
        attributes: {
          'product.taxonCode': props.taxonCode,
          'product.page': props.page,
          'product.itemsPerPage': props.itemsPerPage,
        },
      })
    );

  return { getProductsByTaxon, GetProductsByTaxonError };
};
